import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';

const SocketContext = createContext(null);

export function SocketProvider({ children }) {
  const { user } = useAuth();
  const [connected, setConnected] = useState(false);
  const socketRef = useRef(null);
  const listenersRef = useRef(new Map()); // event -> Set of callbacks

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!user || !token) {
      // Disconnect if logged out
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        setConnected(false);
      }
      return;
    }

    const baseURL = (process.env.REACT_APP_API_URL || 'http://localhost:5001/api').replace('/api', '');

    const socket = io(baseURL, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 2000,
      reconnectionAttempts: Infinity
    });

    socket.on('connect', () => {
      setConnected(true);
    });

    socket.on('disconnect', () => {
      setConnected(false);
    });

    // Relay all events to registered listeners
    const knownEvents = [
      'message:incoming',
      'message:outgoing',
      'message:sent',
      'message:status',
      'conversation:updated',
      'session:qr',
      'session:connected',
      'session:status'
    ];

    knownEvents.forEach((event) => {
      socket.on(event, (data) => {
        const callbacks = listenersRef.current.get(event);
        if (callbacks) {
          callbacks.forEach((cb) => cb(data));
        }
      });
    });

    socketRef.current = socket;

    return () => {
      socket.disconnect();
      socketRef.current = null;
      setConnected(false);
    };
  }, [user]);

  /**
   * Subscribe to a socket event. Returns an unsubscribe function.
   * Usage: const unsub = subscribe('message:incoming', (data) => { ... });
   */
  const subscribe = (event, callback) => {
    if (!listenersRef.current.has(event)) {
      listenersRef.current.set(event, new Set());
    }
    listenersRef.current.get(event).add(callback);

    return () => {
      const set = listenersRef.current.get(event);
      if (set) {
        set.delete(callback);
        if (set.size === 0) listenersRef.current.delete(event);
      }
    };
  };

  return (
    <SocketContext.Provider value={{ connected, subscribe }}>
      {children}
    </SocketContext.Provider>
  );
}

export function useSocket() {
  return useContext(SocketContext);
}
